import { clsx } from "clsx";
import { type PropsWithChildren, type ReactElement, useCallback, useEffect } from "react";
import { type Accept, type FileRejection, useDropzone } from "react-dropzone";
import { shallowEqual, useSelector } from "react-redux";
import { SymbolIcon } from "widgets";
import { humanReadableFileSize } from "widgets/utils";

import { type IAppState } from "../../../redux/reducer.js";
import { ContentPlaceholder } from "../ContentPlaceholder.js";

export interface IFileDropzoneProps {
  showAlways: boolean; // when there is not item yet for example
  disabled?: boolean;
  notAuthorized?: boolean;
  dropzoneContent?: ReactElement;
  acceptedTypes?: Accept;

  addResources: (files: File[]) => void;
  setDropZoneOpenCallback?: (callback: () => void) => void;
  showFileDropWarningMessage: (message: string) => void;
}

export default function FileDropzone({
  showFileDropWarningMessage,
  notAuthorized,
  addResources,
  acceptedTypes,
  disabled,
  setDropZoneOpenCallback,
  showAlways,
  dropzoneContent,
  children,
}: PropsWithChildren<IFileDropzoneProps>) {
  const maxUploadSizeInBytes = useSelector<IAppState, number>(
    (state) => state.config.configuration.application?.maxDiskBuffer ?? Number.MAX_SAFE_INTEGER,
    shallowEqual,
  );
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (notAuthorized) {
        showFileDropWarningMessage(`You are not authorized to upload files in this location`);
        return;
      }
      if (acceptedFiles.length > 0) {
        addResources(acceptedFiles); // let the parent check name clash
      }
      if (fileRejections.length > 0) {
        const rejectedFileNames: string = fileRejections
          .map((fileRejection: FileRejection) => fileRejection.file)
          .map((f) => f.name)
          .join(", ");
        const { size, unit } = humanReadableFileSize(maxUploadSizeInBytes);
        showFileDropWarningMessage(
          `Could not add the following files, please make sure they are of the expected type and have reasonable size (< ${size} ${unit}): ${rejectedFileNames}`,
        );
      }
    },
    [notAuthorized, showFileDropWarningMessage, addResources, maxUploadSizeInBytes],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    onDrop,
    minSize: 0,
    maxSize: maxUploadSizeInBytes,
    accept: acceptedTypes,
    disabled: disabled,
    noClick: true,
  });

  useEffect(() => {
    setDropZoneOpenCallback?.(open);
  }, [open, setDropZoneOpenCallback]);

  const dropzoneClasses = clsx({
    "visible-dropzone": isDragActive,
    "active-dropzone": isDragActive,
    "reject-dropzone": notAuthorized || isDragReject,
  });

  return (
    <div
      className={`dropzone-area ${dropzoneClasses}`}
      style={{ outline: 0 }}
      {...getRootProps({
        onClick: (event) => event.stopPropagation(),
      })}
    >
      <input {...getInputProps()} accept={acceptedTypes ? Object.values(acceptedTypes).join(",") : undefined} />
      {isDragActive || showAlways ? (
        <div className={`dropzone ${dropzoneClasses}`}>
          {dropzoneContent ?? (
            <ContentPlaceholder
              text={
                <>
                  <SymbolIcon icon="cloud_upload" aria-hidden="true" size={40} />
                  <br />
                  Drag and drop your files here to upload
                </>
              }
            />
          )}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
